<script setup lang="ts">
import { eventBus } from '@/libs/EventBus';
import { onMounted, onUnmounted, ref, onErrorCaptured } from 'vue';
import PrinterConfirm from '@/components/PrinterConfirm.vue';
import InternetDisconnected from '@/components/InternetDisconnected.vue';
import { ElNotification } from 'element-plus';
import { useI18n } from 'vue-i18n';
import { captureException } from '@sentry/vue';
import { Close, Warning } from '@element-plus/icons-vue';
import { useInvoiceStore } from '@/stores/invoice';
import { useSectionStore } from '@/stores/section';
import { useAcl } from '@/stores/acl';
import { useConfigStore } from '@/stores/config';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import { notify } from '@/utils/notification';
import { TextToSpeech } from '@capacitor-community/text-to-speech';
import { getPlatform } from '@/utils/misc';

const { t: $t, locale } = useI18n();
const visible = ref(false);
const isOnline = ref(true);
const configStore = useConfigStore();
const QR_ORDER_ANNOUNCE_DEDUP_MS = 30000;
const MAX_ANNOUNCEMENTS = 200; // LRU hard cap to prevent unbounded growth
const recentAnnouncements = new Map<string, number>();

const handleNodeDetected = async () => {
  visible.value = true;
};

// Voice announce QR order (main device only, with 30s dedup)
const handleQrOrderReceived = (data: { uid: string; table_label?: string; call_num?: string }) => {
  if (secureStorage.getCachedItem('is_main_device') !== 'true') return;
  if (!(configStore.appConfig?.config?.online_order as any)?.qr_order_voice_enabled) return;
  const now = Date.now();
  for (const [uid, ts] of recentAnnouncements)
    if (now - ts >= QR_ORDER_ANNOUNCE_DEDUP_MS) recentAnnouncements.delete(uid);
  if (recentAnnouncements.has(data.uid)) return;
  if (recentAnnouncements.size >= MAX_ANNOUNCEMENTS) recentAnnouncements.delete(recentAnnouncements.keys().next().value!);
  recentAnnouncements.set(data.uid, now);
  const sep = locale.value === 'zh' ? '，' : ', ';
  let text = $t('qr_order.new_order');
  if (data.table_label) text += `${sep}${$t('qr_order.table')} ${data.table_label}`;
  else if (data.call_num) text += `${sep}${$t('qr_order.call_num')} ${data.call_num}`;
  notify.info(text);
  const lang = ({ zh: 'zh-CN', vi: 'vi-VN', en: 'en-US', id: 'id-ID' } as Record<string, string>)[locale.value] ?? 'zh-CN';
  const platform = getPlatform();
  if (platform === 'android' || platform === 'ios') {
    TextToSpeech.speak({ text, lang, rate: 0.8 }).catch(() => {});
  } else if ('speechSynthesis' in window) {
    const speech = new SpeechSynthesisUtterance(text);
    speech.lang = lang;
    speech.rate = 0.8;
    window.speechSynthesis.speak(speech);
  }
};

const handleConnectedChange = async (status: boolean) => {
  isOnline.value = status;
};

// Check storage quota and warn if > 90% used
const checkStorageQuota = async () => {
  if (!navigator.storage?.estimate) return
  const { quota, usage } = await navigator.storage.estimate()
  if (!usage || !quota) return
  const usagePercent = Math.round((usage / quota) * 100)
  if (usagePercent > 90) {
    ElNotification({
      customClass: 'x-notification__error',
      title: $t('storage.low_space_title'),
      message: $t('storage.low_space_message', { percent: usagePercent }),
      duration: 0, // persistent until user closes
      icon: Warning,
    })
  }
}

onMounted(() => {
  eventBus.on('node_detected', handleNodeDetected);
  eventBus.on('connectedChange', handleConnectedChange);
  eventBus.on('qr_order_received', handleQrOrderReceived);
  checkStorageQuota();
  // Initialize global stores for CRM package bridge (Pinia guaranteed active after mount)
  (window as any).__INVOICE_STORE__ = useInvoiceStore();
  (window as any).__SECTION_STORE__ = useSectionStore();
  (window as any).__ACL_STORE__ = useAcl();
});

onUnmounted(() => {
  eventBus.off('node_detected', handleNodeDetected);
  eventBus.off('connectedChange', handleConnectedChange);
  eventBus.off('qr_order_received', handleQrOrderReceived);
  recentAnnouncements.clear();
});

onErrorCaptured((err) => {
  if (/dynamically imported module|Importing a module script failed|Unable to preload CSS/.test((err as Error)?.message)) return // Chunk load failure: vite:preloadError already notifies; still reported to Sentry
  ElNotification({
    customClass: 'x-notification__error',
    title: $t('common.error'),
    message: (err as Error).message,
    duration: 7000,
    // type: 'error',
    icon: Close,
  });
});
</script>

<template>
  <router-view></router-view>
  <PrinterConfirm v-model="visible" />
  <InternetDisconnected v-model="isOnline" />
</template>

<style lang="scss">
.el-message-box__status {
  top: 0 !important;
  transform: none !important;
}

.el-select__wrapper {
  height: 50px;
  box-shadow: 0 0 0 1px #a7a7a7 inset !important;
}

.el-select-dropdown__item {
  color: #111827;
  height: 50px !important;
  line-height: 50px !important;
  font-weight: 500;
  border-radius: 5px;
  transition: background-color 0.3s ease;
}
</style>
